import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { AnalyticsService } from '../../core/services/analytics.service';
import { UserAnalytics, SortField, SortDirection } from '../../shared/models';

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css'],
})
export class AnalyticsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  users: UserAnalytics[] = [];
  filteredUsers: UserAnalytics[] = [];
  isLoading = true;
  error: string | null = null;
  searchTerm = '';

  // Sorting
  sortField: SortField = 'wordsUsed';
  sortDirection: SortDirection = 'desc';

  private subscription?: Subscription;

  constructor(private analyticsService: AnalyticsService) {}

  ngOnInit(): void {
    // Don't load on init, wait for modal to open
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Load data when modal opens
    if (changes['isOpen'] && this.isOpen && !changes['isOpen'].previousValue) {
      console.log('📊 Analytics modal opening...');
      console.log('📱 Platform:', Capacitor.getPlatform());
      this.loadUsers();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  loadUsers(): void {
    this.isLoading = true;
    this.error = null;

    this.subscription = this.analyticsService.getAllUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.applyFiltersAndSort();
        this.isLoading = false;
        console.log(`✅ Loaded ${users.length} users for analytics`);
      },
      error: (error) => {
        this.error = error.message || 'Failed to load user analytics';
        this.isLoading = false;
        console.error('❌ Error loading users:', error);
      },
    });
  }

  applyFiltersAndSort(): void {
    // First filter
    let result = this.analyticsService.filterUsers(this.users, this.searchTerm);

    // Then sort
    result = this.analyticsService.sortUsers(
      result,
      this.sortField,
      this.sortDirection
    );

    this.filteredUsers = result;
  }

  onSearchChange(): void {
    this.applyFiltersAndSort();
  }

  sortBy(field: SortField): void {
    // If clicking the same field, toggle direction
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New field, default to ascending (except for usage/payment fields which default to descending)
      this.sortField = field;
      const descFields: SortField[] = ['wordsUsed', 'imagesUsed', 'totalPaymentAmount', 'lastActiveAt'];
      this.sortDirection = descFields.includes(field) ? 'desc' : 'asc';
    }

    this.applyFiltersAndSort();
  }

  getSortIcon(field: SortField): string {
    if (this.sortField !== field) {
      return '↕️'; // No sort
    }
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  }

  getUsageBarWidth(user: UserAnalytics): number {
    const wordUsagePercentage = this.calculateWordUsagePercentage(user);
    return Math.min(wordUsagePercentage, 100);
  }

  getUsageBarColor(user: UserAnalytics): string {
    const wordUsagePercentage = this.calculateWordUsagePercentage(user);
    if (user.isOverLimit || wordUsagePercentage >= 100) {
      return 'bg-red-500';
    }
    if (wordUsagePercentage >= 80) {
      return 'bg-yellow-500';
    }
    return 'bg-green-500';
  }

  calculateWordUsagePercentage(user: UserAnalytics): number {
    if (!user.wordsLimit || user.wordsLimit === 0) {
      return 0;
    }
    return (user.wordsUsed / user.wordsLimit) * 100;
  }

  getWordUsagePercentage(user: UserAnalytics): number {
    return this.calculateWordUsagePercentage(user);
  }

  calculateImageUsagePercentage(user: UserAnalytics): number {
    if (!user.imagesLimit || user.imagesLimit === 0) {
      return 0;
    }
    return (user.imagesUsed || 0) / user.imagesLimit * 100;
  }

  getImageUsagePercentage(user: UserAnalytics): number {
    return this.calculateImageUsagePercentage(user);
  }

  getProviderBadgeClass(provider: string): string {
    switch (provider) {
      case 'Facebook':
        return 'bg-blue-100 text-blue-800';
      case 'Google':
        return 'bg-red-100 text-red-800';
      case 'Email/Password':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  }
}
